import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { ArrowRight, CheckCircle2, Clock, DollarSign, FileText, Send } from "lucide-react"

export const Route = createFileRoute("/features/invoicing")({
  component: InvoicingPage,
})

const FEATURES = [
  {
    icon: FileText,
    color: "bg-blue-light",
    iconColor: "text-blue",
    title: "Beautiful invoice templates",
    desc: "Customise with your logo, brand colours, and payment terms. Customers pay directly from the invoice.",
    items: [
      "Custom branding & logo",
      "Multi-currency support",
      "PDF & hosted versions",
      "Tax line-item support",
    ],
  },
  {
    icon: Send,
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
    title: "Automated delivery",
    desc: "Send invoices by email automatically on a schedule or trigger them via API from your own systems.",
    items: [
      "Email delivery",
      "API-triggered sending",
      "Scheduled invoicing",
      "Customer portal access",
    ],
  },
  {
    icon: Clock,
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
    title: "Smart reminders",
    desc: "Automated payment reminders before and after due dates, fully configurable to your payment terms.",
    items: [
      "Pre-due reminders",
      "Overdue escalation",
      "Configurable schedules",
      "Stopped on payment",
    ],
  },
  {
    icon: DollarSign,
    color: "bg-rose-light",
    iconColor: "text-rose-DEFAULT",
    title: "Flexible payment options",
    desc: "Customers can pay by card, bank transfer, or any local method you have enabled in your account.",
    items: ["Pay by card", "Bank transfer", "SEPA / ACH", "Partial payments"],
  },
]

function InvoicingPage() {
  useSEO({
    title: "Invoicing",
    description:
      "Automated invoice generation, delivery, and collection. Beautiful branded invoices with built-in payment links.",
    canonical: "https://orchestrate.global/features/invoicing",
  })
  return (
    <>
      <section className="bg-surface grid-bg relative overflow-hidden pt-32 pb-20">
        <div className="hero-glow" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <AnimatedSection>
                <SectionLabel label="Invoicing" className="mb-5" />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight sm:text-6xl lg:text-7xl">
                  Invoice, collect,
                  <br />
                  <em className="text-blue not-italic">get paid faster.</em>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="text-muted mb-10 text-xl leading-relaxed">
                  Generate beautiful branded invoices, deliver them automatically, and collect
                  payment via any method — all without leaving Orchestrate.
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" size="lg" asChild>
                    <Link to="/contact">
                      Get started <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/docs">API reference</Link>
                  </Button>
                </div>
              </AnimatedSection>
            </div>
            <AnimatedSection direction="right" delay={0.2}>
              {/* Invoice mockup */}
              <div className="border-border rounded-2xl border bg-white p-8 shadow-xl">
                <div className="mb-8 flex items-start justify-between">
                  <div>
                    <div className="bg-blue mb-3 h-8 w-8 rounded-lg" />
                    <p className="text-ink font-bold">Acme Corp</p>
                    <p className="text-muted text-xs">123 Business St, London</p>
                  </div>
                  <div className="text-right">
                    <p className="text-muted text-xs font-semibold tracking-widest uppercase">
                      Invoice
                    </p>
                    <p className="font-display text-ink text-2xl">#INV-0042</p>
                    <span className="bg-green-light text-green-DEFAULT rounded-full px-2 py-0.5 text-xs font-semibold">
                      Paid
                    </span>
                  </div>
                </div>
                <div className="border-border mb-6 space-y-2 border-t pt-4">
                  {[
                    { item: "Platform subscription — Pro", qty: "1", price: "$299.00" },
                    { item: "Additional seats (×3)", qty: "3", price: "$90.00" },
                    { item: "Setup fee", qty: "1", price: "$150.00" },
                  ].map((row) => (
                    <div key={row.item} className="flex items-center justify-between text-sm">
                      <span className="text-muted">{row.item}</span>
                      <span className="font-medium">{row.price}</span>
                    </div>
                  ))}
                </div>
                <div className="border-border border-t pt-4">
                  <div className="text-muted mb-1 flex justify-between text-xs">
                    <span>Subtotal</span>
                    <span>$539.00</span>
                  </div>
                  <div className="text-muted mb-3 flex justify-between text-xs">
                    <span>VAT (20%)</span>
                    <span>$107.80</span>
                  </div>
                  <div className="flex justify-between text-base font-bold">
                    <span>Total</span>
                    <span className="text-blue">$646.80</span>
                  </div>
                </div>
                <div className="bg-blue mt-5 rounded-xl py-2.5 text-center text-sm font-semibold text-white">
                  Pay invoice
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-14 text-center">
              <SectionLabel label="Features" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                Everything invoicing<em className="text-blue not-italic"> needs to be</em>
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.09, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  <div className="border-border h-full rounded-2xl border bg-white p-6 transition-all duration-250 hover:-translate-y-1.5 hover:shadow-xl">
                    <div
                      className={`h-11 w-11 ${f.color} mb-4 flex items-center justify-center rounded-xl`}
                    >
                      <Icon className={`h-5 w-5 ${f.iconColor}`} />
                    </div>
                    <h3 className="mb-2 text-base font-semibold">{f.title}</h3>
                    <p className="text-muted mb-4 text-xs leading-relaxed">{f.desc}</p>
                    <ul className="space-y-1.5">
                      {f.items.map((it) => (
                        <li key={it} className="text-muted flex items-center gap-2 text-xs">
                          <CheckCircle2 className="text-green-DEFAULT h-3.5 w-3.5 flex-shrink-0" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <AnimatedSection>
            <SectionLabel label="Metrics" centered className="mb-5" />
            <h2 className="font-display mb-14 text-4xl tracking-tight sm:text-5xl">
              Invoicing that performs
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-3 gap-8">
            {[
              { val: "4.2 days", label: "Avg time to payment", sub: "vs 14-day industry average" },
              { val: "94%", label: "Collection rate", sub: "with automated reminders" },
              { val: "2 min", label: "To send first invoice", sub: "from account creation" },
            ].map((s, i) => (
              <AnimatedSection key={s.label} delay={i * 0.1}>
                <div className="border-border rounded-2xl border bg-white p-6">
                  <p className="font-display text-blue mb-2 text-3xl">{s.val}</p>
                  <p className="mb-1 text-sm font-semibold">{s.label}</p>
                  <p className="text-muted text-xs">{s.sub}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  )
}
